import {
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@folio/components';
import { sanitize } from '@folio/utils';
import { useNewBusinessProductIds } from '@folio/hooks/useNewBusinessProductIds';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { type InitializeCustomer, useCustomerControllerInitializeCustomer } from 'korint-sdk';
import { useKorintConfig, useKorintProductConfig } from 'korint-sdk/react';
import { sortBy } from 'lodash';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Group } from '@folio/components/ui/flex';

const PRODUCT_SELECTION_PAGE_THRESHOLD = 4;

export function NewCustomerButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const { productsConfig } = useKorintProductConfig();
  const { korintConfig } = useKorintConfig();
  const { productIds, isLoading } = useNewBusinessProductIds();

  const { can } = useUserPermissions({ actions: ['INITIALIZE_CUSTOMER'] });

  const { mutate: initCustomer, isPending } = useCustomerControllerInitializeCustomer({
    mutation: {
      onSuccess: (customer: InitializeCustomer, variables: { params: { productId: string } }) => {
        const productId = variables.params.productId;
        const productConfig = productsConfig[productId];
        const productCustomPath = productConfig?.productCustomPath ?? '';

        navigate(
          `/discovery${productCustomPath}/${customer.id}/${customer.initializedWith.policy}/${customer.initializedWith.branch}`,
        );
      },
    },
  });

  if (isLoading || !productIds?.length || !can.INITIALIZE_CUSTOMER) {
    return null;
  }

  if (productIds.length === 1) {
    return (
      <Button
        size="sm"
        leftIcon={<PlusIcon className="size-4" />}
        loading={isPending}
        onClick={() => initCustomer({ data: {}, params: { productId: productIds[0] } })}
      >
        {t('products.title')}
      </Button>
    );
  }

  if (productIds.length >= PRODUCT_SELECTION_PAGE_THRESHOLD) {
    return (
      <Button
        size="sm"
        leftIcon={<PlusIcon className="size-4" />}
        onClick={() => navigate('/new-business')}
      >
        {t('products.title')}
      </Button>
    );
  }

  // Opt-in per tenant: with only 2 or 3 products a searchable list is pure
  // overhead, so each product gets its own button, icon and label. The label
  // falls back to the full one when the product declares no shortLabel, and
  // drops out entirely on narrow viewports where a row of labelled buttons
  // would not fit next to the page title.
  if (korintConfig.newBusinessButtonPerProduct) {
    return (
      <Group wrap gap={2}>
        {sortBy(productIds, productId => t(`products.${productId}.label`)).map(productId => {
          const label = t(`products.${productId}.label`);
          const Icon = productsConfig[productId]?.display?.icon as
            | React.ComponentType<{ className?: string }>
            | undefined;

          return (
            <Button
              key={productId}
              size="sm"
              title={label}
              // Below `sm` only the icon shows, so the button needs its own
              // accessible name, and the icon's trailing padding has to go or it
              // sits off-centre.
              aria-label={label}
              className="[&>div:first-child]:pr-0 sm:[&>div:first-child]:pr-2"
              loading={isPending}
              leftIcon={Icon ? <Icon className="size-4" /> : <PlusIcon className="size-4" />}
              onClick={() => initCustomer({ data: {}, params: { productId } })}
            >
              <span className="hidden sm:inline">
                {t(`products.${productId}.shortLabel`, { defaultValue: label })}
              </span>
            </Button>
          );
        })}
      </Group>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" leftIcon={<PlusIcon className="size-4" />} loading={isPending}>
          {t('products.title')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        alignOffset={0}
        className="p-0 min-w-md"
        side="bottom"
        avoidCollisions
        collisionPadding={8}
      >
        <Command onValueChange={() => setOpen(false)} shouldFilter={false}>
          <CommandInput
            placeholder={t('products.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {sortBy(
              productIds.filter(productId =>
                sanitize(t(`products.${productId}.label`)).includes(sanitize(search)),
              ),
              productId => t(`products.${productId}.label`),
            ).map(productId => (
              <CommandItem
                value={productId}
                key={productId}
                onSelect={() => initCustomer({ data: {}, params: { productId } })}
              >
                {t(`products.${productId}.label`)}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
